import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import { adminAuthService } from '../services/adminAuth.service';

export const AuthCallback = () => {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [hasCompletedProcess, setHasCompletedProcess] = useState(false);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Wait for MSAL to initialize before proceeding
        if (!instance || inProgress !== InteractionStatus.None) {
          return; // Still initializing, wait for next effect cycle
        }

        // Handle the redirect promise
        const response = await instance.handleRedirectPromise();

        if (response) {
          // Get the ID token from the response
          const idToken = response.idToken;

          // Get the stored source URL for redirect
          const storedSourceUrl = sessionStorage.getItem('admin_source_url');

          // Send the token to Django backend for validation
          const validationResponse = await adminAuthService.validateAzureToken({
            id_token: idToken,
            access_token: response.accessToken,
            redirect_url: storedSourceUrl || undefined,
          });

          if (validationResponse.success && validationResponse.redirect_url) {
            // Clear all session storage items
            sessionStorage.clear();

            // Mark process as completed before redirect to prevent error flash
            setHasCompletedProcess(true);
            setIsProcessing(false);

            // Small delay to ensure state update, then redirect
            setTimeout(() => {
              window.location.href = validationResponse.redirect_url!;
            }, 100);
          } else {
            setError(validationResponse.error || 'Authentication failed');
            setIsProcessing(false);
          }
        } else if (accounts.length > 0) {
          // User is already logged in, get a token silently
          const account = accounts[0];
          const tokenResponse = await instance.acquireTokenSilent({
            account,
            scopes: ['openid', 'profile'],
          });

          // Get the stored source URL for redirect
          const storedSourceUrl = sessionStorage.getItem('admin_source_url');

          // Validate with backend
          const validationResponse = await adminAuthService.validateAzureToken({
            id_token: tokenResponse.idToken,
            access_token: tokenResponse.accessToken,
            redirect_url: storedSourceUrl || undefined,
          });

          if (validationResponse.success && validationResponse.redirect_url) {
            // Clear all session storage items
            sessionStorage.clear();

            // Mark process as completed before redirect to prevent error flash
            setHasCompletedProcess(true);
            setIsProcessing(false);

            // Small delay to ensure state update, then redirect
            setTimeout(() => {
              window.location.href = validationResponse.redirect_url!;
            }, 100);
          } else {
            setError(validationResponse.error || 'Authentication failed');
            setIsProcessing(false);
          }
        } else {
          // No response and no accounts, redirect to login
          navigate('/login');
        }
      } catch (err) {
        // Handle authentication error by showing user-friendly message
        const errorMessage =
          err instanceof Error ? err.message : 'Authentication failed. Please try again.';
        setError(errorMessage);
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [instance, accounts, navigate, inProgress]);

  // Only show errors if we haven't completed the process successfully
  if (error && !hasCompletedProcess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Authentication Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-sm font-medium text-red-600 hover:text-red-500"
                  >
                    Return to login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div
        className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
        style={{
          background: 'url(/assets/gti/background.jpg)',
          backgroundSize: 'cover',
        }}
      >
        <div className="text-center bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto">
          <svg
            className="animate-spin h-12 w-12 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            style={{ color: 'var(--mantine-color-yellow-5)' }}
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: 'var(--mantine-color-navy-8)' }}
          >
            Authentication in progress
          </h2>
          <p className="text-sm" style={{ color: 'var(--mantine-color-navy-6)' }}>
            You'll be redirected shortly. Please wait...
          </p>
        </div>
      </div>
    );
  }

  return null;
};
